import { useCallback, useRef } from "react";

// Problem statements are HTML with images served from another origin (the
// backend's uploads or a bucket) and KaTeX formulas rendered client-side.
// Re-creating that DOM every time the statement is shown re-fetches the
// images and re-renders the formulas, which is what makes toggling it slow.
//
// So each statement's DOM is built once and kept here; the floating modal,
// the docked panel and the preview drawer adopt the same element when they
// show it. Moving an element between parents never reloads its images.

const MAX_CACHED = 20;

// Insertion order doubles as LRU order: a hit is re-inserted at the end.
const cache = new Map<string, HTMLDivElement>();

async function renderFormulas(root: HTMLElement): Promise<void> {
    const elements = root.querySelectorAll<HTMLElement>("span.katex[text]");
    if (elements.length === 0) return;
    const showRaw = () =>
        elements.forEach((element) => {
            const latex = element.getAttribute("text");
            if (latex) element.textContent = latex;
        });
    try {
        const katex = (await import("katex")).default;
        elements.forEach((element) => {
            const latex = element.getAttribute("text");
            if (!latex) return;
            try {
                katex.render(latex, element, {
                    throwOnError: false,
                    displayMode: false,
                });
            } catch (error) {
                console.error("KaTeX rendering error:", error);
                element.textContent = latex;
            }
        });
    } catch (error) {
        console.warn("KaTeX not available:", error);
        showRaw();
    }
}

/**
 * The statement's DOM, built on first use and reused afterwards. The
 * statement HTML is trusted content from the problem's author, exactly as it
 * was with dangerouslySetInnerHTML before.
 */
export function getStatementElement(statement: string): HTMLDivElement {
    const cached = cache.get(statement);
    if (cached) {
        cache.delete(statement);
        cache.set(statement, cached);
        return cached;
    }
    const element = document.createElement("div");
    element.className = "problem-content";
    element.innerHTML = statement;
    void renderFormulas(element);
    cache.set(statement, element);
    if (cache.size > MAX_CACHED) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
    }
    return element;
}

/** Builds the statement (and starts loading its images) before it is shown. */
export function preloadStatement(statement?: string | null): void {
    if (statement && typeof document !== "undefined") {
        getStatementElement(statement);
    }
}

/** @internal Tests only. */
export function clearStatementCache(): void {
    cache.clear();
}

interface Attachment {
    host: HTMLElement;
    node: HTMLElement;
}

/**
 * Ref callback for the element that shows the statement. The cached DOM is
 * moved into the host whenever one is mounted, and taken out when it
 * unmounts, so a window that renders nothing while closed gets the content
 * back each time it opens. A second host showing the same statement at the
 * same time (the preview drawer next to a docked panel) gets a copy instead.
 */
export function useStatementHost(statement?: string | null) {
    const attachedRef = useRef<Attachment | null>(null);

    return useCallback(
        (host: HTMLDivElement | null) => {
            const attached = attachedRef.current;
            if (attached) {
                if (attached.node.parentNode === attached.host) {
                    attached.host.removeChild(attached.node);
                }
                attachedRef.current = null;
            }
            if (!host || !statement) return;
            const element = getStatementElement(statement);
            const node =
                element.parentNode && element.parentNode !== host
                    ? (element.cloneNode(true) as HTMLDivElement)
                    : element;
            host.replaceChildren(node);
            attachedRef.current = { host, node };
        },
        [statement],
    );
}
